/* eslint-disable @typescript-eslint/no-unused-vars */

import { useEffect, useRef, useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { toast } from 'sonner'
import Toast from '@common/components/Toast'
import { UseMutationResult } from '@tanstack/react-query'
import { queryClient } from '@common/config/api/queryClient'
import { userManager } from '@auth/oidc.config'
import { SIGNING_INTENT_READY_EVENT } from '@auth/utils/requestEsignReauthentication'

type SigningContext = {
  type?: string
  [key: string]: unknown
}

type EsignBody<TContext extends SigningContext> = {
  auth_token: string
  signer_name: string
  reason?: string | null
  context: TContext
}

type EsignRequest<TContext extends SigningContext> = {
  body: EsignBody<TContext>
  path?: Record<string, string>
  query?: Record<string, unknown>
}

type StoredSigningParams = {
  pathParams?: Record<string, string>
  queryParams?: Record<string, unknown>
  signer_name: string
  reason?: string
  [key: string]: unknown
}

type SigningProcessOptions = {
  contextType?: string
  successMessage: string
  errorMessage: string
  onSuccess?: (response: unknown) => void
  onError?: (error: unknown) => void
}

export function useRestoreSigningProcess<
  TContext extends SigningContext,
  TRequest extends EsignRequest<TContext>,
  TResponse,
>(mutationHook: UseMutationResult<TResponse, unknown, TRequest>, options: SigningProcessOptions) {
  const {
    contextType,
    successMessage = 'Signed successfully',
    errorMessage = 'Failed to complete signing process',
    onSuccess,
    onError,
  } = options

  const { user, isAuthenticated } = useAuth()
  const processingRef = useRef(false)
  const [isSigningInProgress, setIsSigningInProgress] = useState(false)
  const [hasCompletedSigning, setHasCompletedSigning] = useState(false)
  const [signingError, setSigningError] = useState<unknown | null>(null)
  const [signingResponse, setSigningResponse] = useState<TResponse | null>(null)
  // Bumped when popup re-auth stores intent after the OIDC user is already updated,
  // so restore still runs without relying on a user-object change.
  const [signingIntentEpoch, setSigningIntentEpoch] = useState(0)

  useEffect(() => {
    setHasCompletedSigning(false)
    setSigningError(null)
    setSigningResponse(null)
  }, [mutationHook, contextType])

  useEffect(() => {
    const onSigningIntentReady = () => {
      setSigningIntentEpoch((epoch) => epoch + 1)
    }

    window.addEventListener(SIGNING_INTENT_READY_EVENT, onSigningIntentReady)
    return () => {
      window.removeEventListener(SIGNING_INTENT_READY_EVENT, onSigningIntentReady)
    }
  }, [])

  useEffect(() => {
    const checkForSigningIntent = async () => {
      if (processingRef.current) return

      const signingPayload = localStorage.getItem('signing_payload')
      const signingParams = localStorage.getItem('signing_params')

      if (!signingPayload || !signingParams || !isAuthenticated) {
        return
      }

      let payload: TContext
      try {
        payload = JSON.parse(signingPayload) as TContext
      } catch {
        return
      }

      // Do not claim intents meant for a different restore hook instance.
      if (contextType && payload.type !== contextType) {
        return
      }

      // Claim synchronously before any await so concurrent effect runs
      // (signingIntentEpoch + OIDC user update) cannot both start mutateAsync.
      processingRef.current = true
      setIsSigningInProgress(true)
      setHasCompletedSigning(false)
      setSigningError(null)

      try {
        // Prefer the UserManager store so popup re-auth tokens are used even if
        // React auth context has not re-rendered yet.
        const storedUser = await userManager.getUser()
        const accessToken = storedUser?.access_token ?? user?.access_token

        if (!accessToken) {
          return
        }

        const params = JSON.parse(signingParams) as StoredSigningParams

        const request = {
          body: {
            auth_token: accessToken,
            signer_name: params.signer_name,
            reason: params.reason,
            context: payload,
          },
        } as TRequest

        if (params.pathParams && Object.keys(params.pathParams).length > 0) {
          request.path = params.pathParams as Record<string, string>
        }

        if (params.queryParams && Object.keys(params.queryParams).length > 0) {
          request.query = params.queryParams as Record<string, unknown>
        }

        const response = await mutationHook.mutateAsync(request)

        setSigningResponse(response)

        setHasCompletedSigning(true)

        toast(<Toast variant="success" message={successMessage} />)

        if (onSuccess) {
          onSuccess(response)
        }

        localStorage.removeItem('signing_payload')
        localStorage.removeItem('signing_params')
      } catch (error) {
        console.error('Failed to complete signing process:', error)

        setSigningError(error)

        toast(<Toast variant="error" message={errorMessage} />)

        if (onError) {
          onError(error)
        }

        if (!contextType || payload.type === contextType) {
          localStorage.removeItem('signing_payload')
          localStorage.removeItem('signing_params')
        }
      } finally {
        processingRef.current = false
        setIsSigningInProgress(false)
        queryClient.invalidateQueries({ queryKey: ['SummaryReadTasks'] })
        queryClient.invalidateQueries({ queryKey: ['ReadTasksList'] })
      }
    }

    if (isAuthenticated) {
      checkForSigningIntent()
    }
  }, [
    isAuthenticated,
    user,
    mutationHook,
    successMessage,
    errorMessage,
    contextType,
    onSuccess,
    onError,
    signingIntentEpoch,
  ])

  const hasPendingSigningIntent = (() => {
    const signingPayload = localStorage.getItem('signing_payload')
    const signingParams = localStorage.getItem('signing_params')

    if (!signingPayload || !signingParams) {
      return false
    }

    if (contextType) {
      try {
        const payload = JSON.parse(signingPayload)
        return payload.type === contextType
      } catch (e) {
        return false
      }
    }

    return true
  })()

  return {
    isSigningInProgress: isSigningInProgress || (hasPendingSigningIntent && isAuthenticated),
    hasCompletedSigning,
    signingError,
    signingResponse,
  }
}
